import { template as _$template } from "solid-js/web";
import { createComponent as _$createComponent } from "solid-js/web";
import { memo as _$memo } from "solid-js/web";
var _tmpl$ = /*#__PURE__*/_$template(`<p class=duel__waiting data-testid=duel-waiting>WAITING FOR OPPONENT…`);
import { createSignal, onCleanup, onMount, Show } from "solid-js";
import { browserSettingsStore, DEFAULT_AUDIO_SETTINGS } from "../audio/settings.ts";
import { createDashCanvasFeed } from "../flight/cockpit-dash-quad.ts";
import { cockpit3dDrawn } from "../flight/scene-camera.ts";
import { FlightSurface } from "../flight/FlightSurface.tsx";
import { createBootStatus } from "../flight/boot-status.ts";
import { cameraModeForCommand, createFlightScene, DEFAULT_CAMERA_MODE, IDLE_COCKPIT_VIEW } from "../flight/flight-scene.ts";
import { flightRegionChips } from "../flight/fly-region.ts";
import { realismSummary } from "../flight/hud-format.ts";
import { createFlightInputSession } from "../flight/input-session.ts";
import { createPersistedFlightSettings } from "../flight/persisted-settings.ts";
import { DuelOutcomeOverlay } from "./DuelOutcomeOverlay.tsx";

/**
 * A duel in the air: the same scene, HUD and controls as `/fly`, mounted on
 * the lockstep runner instead of a locally-built one.
 *
 * The only thing on screen that `/fly` has no equivalent of is the
 * "WAITING FOR OPPONENT" line — a stalled tick is the one failure mode of
 * input-delay lockstep the player has to be told about, because otherwise a
 * peer with a bad connection just looks like a game that hitches for no reason.
 */

const CAMERA_LABELS = {
  cockpit: "3 · COCKPIT VIEW",
  chase: "1 · CHASE CAMERA",
  flyBy: "2 · FLY-BY CAMERA"
};
const MESSAGE_HISTORY = 3;

/**
 * The duel's own reading of the flight commands, and its two deliberate
 * refusals. A factory rather than a method on `DuelFlight`, for the reason
 * `Fly.tsx`'s `createRunCommand` is one — the component is at the file-size
 * gate's ceiling — and `scene` is read through a getter because the component's
 * own binding is a plain `let`, reassigned once `onMount` returns.
 *
 * **`pause` is local-only**: pausing a lockstep duel would stall the opponent
 * as surely as pulling the cable out, so it stops this machine's own clock and
 * the stall indicator explains the rest. **PREFERENCES does not pause at all**,
 * deliberately: the panel swallows the keyboard and the joystick while it is up
 * (`flight/PreferencesOverlay.tsx`, `flight/gamepad.ts`), so the aeroplane
 * simply flies on trimmed while its pilot is reading. And **time compression is
 * never wired** — it is a lockstep runner away from a desync, so the `K` key
 * falls silently through here rather than toggling mute by accident.
 */
function createDuelCommand(deps) {
  return command => {
    const mode = cameraModeForCommand(command, deps.cameraMode());
    if (mode) {
      deps.setCameraMode(mode);
      deps.scene()?.setCameraMode(mode);
      return;
    }
    if (command === "pause") {
      const next = !deps.paused();
      deps.setPaused(next);
      deps.scene()?.setPaused(next);
      return;
    }
    if (command === "preferences") {
      deps.setSettingsVisible(visible => !visible);
      return;
    }
    if (command === "timeCompress") return;
    deps.toggleMute();
  };
}
export function DuelFlight(props) {
  // Read off the runner rather than taken as props: the guest flies the tier
  // and the aeroplane the *host's* handshake decided, which its own lobby
  // selection may not match.
  const briefing = () => {
    const config = props.runner.state().config;
    return {
      aircraft: config.spec.name,
      realismChips: realismSummary(config.tier, config.realism)
    };
  };
  const settings = createPersistedFlightSettings();
  // No WebGPU gate — see `Fly.tsx`'s own note and `three/renderer-mode.ts`.
  const boot = createBootStatus("initializing");
  const [hud, setHud] = createSignal(props.runner.hud());
  const [combat, setCombat] = createSignal(props.runner.combat());
  const [region, setRegion] = createSignal(undefined);
  const [messages, setMessages] = createSignal([]);
  const [cameraMode, setCameraMode] = createSignal(DEFAULT_CAMERA_MODE);
  const [cockpitView, setCockpitView] = createSignal(IDLE_COCKPIT_VIEW);
  const [paused, setPaused] = createSignal(false);
  const [hintVisible, setHintVisible] = createSignal(true);
  const [settingsVisible, setSettingsVisible] = createSignal(false);
  let containerRef;
  let scene;
  function applyAudio(next) {
    settings.writeAudio(next);
    scene?.setAudioSettings(next);
  }
  function applyCockpit(next) {
    settings.writeCockpit(next);
  }

  // A duel does **not** pause under PREFERENCES — pausing one lockstep peer
  // stalls both — so `blocked` is the only thing standing between a button
  // pressed on the panel and a burst fired into the sky behind it.
  const input = createFlightInputSession({
    store: browserSettingsStore(),
    onCommand: command => runCommand(command),
    blocked: () => settingsVisible()
  });

  /** AD-012's propeller disc is cosmetic, so a duel peer may toggle it without either side's flight changing. */
  function applyEffects(next) {
    settings.writeEffects(next);
    scene?.setEffectsSettings(next);
  }

  /**
   * AD-007n's stick shaping is a property of *this* keyboard, not of the
   * simulation both peers run, so it is as safe to change mid-duel as the
   * propeller disc is — and it has to be wired, because the PREFERENCES panel
   * offers it here exactly as it does in `/fly`.
   */
  function applyStick(next) {
    settings.writeStick(next);
    scene?.setStickShaping(next.shaping);
  }
  const runCommand = createDuelCommand({
    cameraMode,
    setCameraMode,
    paused,
    setPaused,
    setSettingsVisible,
    toggleMute: () => applyAudio({
      ...settings.audio(),
      muted: !settings.audio().muted
    }),
    scene: () => scene
  });

  /** 3D COCKPIT's shared canvas — see `Fly.tsx`'s own note; a duel is `/fly` with a different source of ticks. */
  const dashFeed = createDashCanvasFeed();
  onMount(() => {
    input.open();
    onCleanup(() => input.close());
    if (!containerRef) return;
    scene = createFlightScene(containerRef, {
      onStatus: boot.report,
      onBootProgress: boot.reportProgress,
      onHud: setHud,
      onCockpitView: setCockpitView,
      onCombat: setCombat,
      onRegion: setRegion,
      onMessages: incoming => setMessages(current => [...current, ...incoming].slice(-MESSAGE_HISTORY)),
      pressedKeys: input.pressedKeys,
      pointer: input.pointer,
      gamepad: input.gamepad,
      // Camera only, and that is load-bearing in a duel: the lockstep wire
      // carries `FlightInputs` and nothing else, so where this peer's pilot is
      // looking cannot reach the other peer's sim (`free-look.ts`).
      freeLook: input.freeLook
    }, {
      audio: settings.audio() ?? DEFAULT_AUDIO_SETTINGS,
      effects: settings.effects(),
      dashFeed,
      runner: props.runner,
      // Lockstep: running `props.runner` ahead of real time would desync
      // the two peers exactly as surely as pausing one side would.
      timeCompressionAvailable: false,
      // A duel is the one competitive thing this port has, and AD-015's
      // window opens itself over a quarter of the screen the moment somebody
      // is hit — an advantage to whichever peer is not the one being watched.
      // It costs a duel nothing to go without: there are two aeroplanes, and
      // the one that goes down ends the fight.
      killCamAvailable: false
    });
    onCleanup(() => {
      scene?.dispose();
      scene = undefined;
    });
  });
  return _$createComponent(FlightSurface, {
    containerRef: element => {
      containerRef = element;
    },
    get hud() {
      return hud();
    },
    get combat() {
      return combat();
    },
    get aircraft() {
      return briefing().aircraft;
    },
    get aircraftKey() {
      return props.runner.state().config.aircraft;
    },
    get cockpit3d() {
      return cockpit3dDrawn(settings.effects(), props.runner.state().config.aircraft);
    },
    dashFeed: dashFeed,
    get chips() {
      return _$memo(() => !!region())() ? flightRegionChips(region()) : [];
    },
    get realismChips() {
      return briefing().realismChips;
    },
    get messages() {
      return messages();
    },
    get cameraLabel() {
      return CAMERA_LABELS[cameraMode()];
    },
    get cameraMode() {
      return cameraMode();
    },
    get cockpitView() {
      return cockpitView();
    },
    get paused() {
      return paused();
    },
    boot: boot,
    get audioSettings() {
      return settings.audio();
    },
    get cockpitSettings() {
      return settings.cockpit();
    },
    onChangeAudio: applyAudio,
    onChangeCockpit: applyCockpit,
    onChangeEffects: applyEffects,
    onChangeStick: applyStick,
    get onReturnToTitle() {
      return props.onExit;
    },
    get gamepad() {
      return input.stick;
    },
    get settingsVisible() {
      return settingsVisible();
    },
    onToggleSettings: () => setSettingsVisible(visible => !visible),
    onDismissSettings: () => setSettingsVisible(false),
    get hintVisible() {
      return hintVisible();
    },
    onDismissHint: () => setHintVisible(false),
    get children() {
      return [_$createComponent(Show, {
        get when() {
          return props.waiting;
        },
        get children() {
          return _tmpl$();
        }
      }), _$createComponent(DuelOutcomeOverlay, {
        get outcome() {
          return props.outcome;
        },
        get canRematch() {
          return props.canRematch;
        },
        get rematchHint() {
          return props.rematchHint;
        },
        get onRematch() {
          return props.onRematch;
        },
        get onExit() {
          return props.onExit;
        }
      })];
    }
  });
}